import type { Filter, FilterDataSwitch } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { DynamicFiltersResponse } from '@/app/components/search/utils/api/dynamicFiltersResponseTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { SUSTAINABILITY_CERTIFICATE_COPY } from '@/app/common/sustainabilityCertificate.ts';
import { getBooleanFromString } from '@/app/components/search/utils/query2.ts';
import { disabledAtZero } from '@/app/common/filters/groups/optionCounts.ts';
import { removePlaceholders } from '@/app/components/search/utils/translationNodes.tsx';

type CreateSustainabilityCertificateFilters = {
  searchParams: SearchParams;
  dynamicFiltersResponse: DynamicFiltersResponse | undefined;
  translationDictionary: TranslationDictionary;
};

export const createSustainabilityCertificateFilters = ({
  searchParams,
  dynamicFiltersResponse,
  translationDictionary,
}: CreateSustainabilityCertificateFilters): Filter[] => {
  const counter = dynamicFiltersResponse?.sustainabilityCertificate;
  // Same parse as the memsearch request (getBooleanFromString), so a deep link with '=true'
  // cannot filter the results while the switch renders unselected.
  const selected = getBooleanFromString(searchParams?.[URLParameters.SUSTAINABILITY_CERTIFICATE] ?? '');
  // Before the filters API answers, show optimistically; once it has answered, a missing facet
  // means the market's backend does not support the filter — fail closed and drop it entirely.
  // A facet that answered 0 is a different case: the filter is supported, this search just has
  // nothing to offer, so it stays visible and greys out.
  const unsupported = !!dynamicFiltersResponse && counter === undefined;
  if (!selected && unsupported) return [];

  const baseLabel = getTranslationServer(SUSTAINABILITY_CERTIFICATE_COPY.filterText, translationDictionary);
  const title = getTranslationServer(SUSTAINABILITY_CERTIFICATE_COPY.filterTitle, translationDictionary);
  if (!baseLabel || !title) return [];

  return [
    {
      name: FILTER_NAME.SUSTAINABILITY_CERTIFICATE,
      type: FILTER_TYPE.SWITCH,
      disabled: disabledAtZero(selected, counter),
      data: {
        label: removePlaceholders(baseLabel),
        labelEmphasis: baseLabel,
        selected,
        counter,
      } as FilterDataSwitch,
    },
  ];
};
